import { useState } from 'react'
import { StatusPill } from '@/components/administration/kpi-metrics/shared/status-pill'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import type { PersonTimelineDirecionada } from '@/types'
import { formatDateToDisplay } from '@/utils/formatDate'
import { TimelineEmptyState } from '../person-timeline-modal'

export function DirecionadaTimelineSection({
	data,
}: {
	data: PersonTimelineDirecionada
}) {
	const [mode, setMode] = useState<'applied' | 'asResponsible'>('applied')

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-2 rounded-lg border border-kaefer-light-silver bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="flex h-3 flex-1 overflow-hidden rounded-full bg-kaefer-light-silver/50">
					<div
						className="bg-kaefer-forest-green"
						style={{
							width: `${(data.compliant / (data.totalAnswers || 1)) * 100}%`,
						}}
					/>
					<div
						className="bg-kaefer-red"
						style={{
							width: `${(data.nonCompliant / (data.totalAnswers || 1)) * 100}%`,
						}}
					/>
					<div
						className="bg-kaefer-medium-grey/40"
						style={{
							width: `${(data.notApplicable / (data.totalAnswers || 1)) * 100}%`,
						}}
					/>
				</div>
				<div className="flex shrink-0 flex-wrap gap-3 text-xs">
					<span className="font-semibold text-kaefer-forest-green">
						{data.compliancePct}% conforme
					</span>
					<span className="text-muted-foreground">
						Conforme: {data.compliant}
					</span>
					<span className="text-muted-foreground">
						Não conforme: {data.nonCompliant}
					</span>
					<span className="text-muted-foreground">
						N/A: {data.notApplicable}
					</span>
				</div>
			</div>

			<Tabs value={mode} onValueChange={(v) => setMode(v as typeof mode)}>
				<TabsList className="w-fit">
					<TabsTrigger value="applied">
						Aplicadas por ele ({data.total})
					</TabsTrigger>
					<TabsTrigger value="asResponsible">
						Como responsável ({data.asResponsible.total})
					</TabsTrigger>
				</TabsList>
			</Tabs>

			{mode === 'applied' ? (
				data.items.length === 0 ? (
					<TimelineEmptyState label="Nenhuma Direcionada aplicada no período." />
				) : (
					<div className="rounded-lg border border-kaefer-light-silver">
						<Table className="table-fixed">
							<TableHeader>
								<TableRow className="bg-muted/30">
									<TableHead className="w-24">Data</TableHead>
									<TableHead className="w-40">Checklist</TableHead>
									<TableHead>Comentário</TableHead>
									<TableHead className="w-20 text-right">Conforme</TableHead>
									<TableHead className="w-24 text-right">
										Não conforme
									</TableHead>
									<TableHead className="w-16 text-right">N/A</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{data.items.map((item) => (
									<TableRow key={item.id}>
										<TableCell className="whitespace-nowrap text-muted-foreground">
											{formatDateToDisplay(item.date)}
										</TableCell>
										<TableCell className="whitespace-normal wrap-break-word">
											{item.quizName}
										</TableCell>
										<TableCell className="whitespace-normal wrap-break-word text-muted-foreground">
											{item.comment ?? '—'}
										</TableCell>
										<TableCell className="text-right">
											{item.compliant}
										</TableCell>
										<TableCell className="text-right">
											{item.nonCompliant}
										</TableCell>
										<TableCell className="text-right">
											{item.notApplicable}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				)
			) : (
				<>
					<p className="text-xs text-muted-foreground">
						{data.asResponsible.resolved} resolvido(s) ·{' '}
						{data.asResponsible.pending} pendente(s)
					</p>
					{data.asResponsible.items.length === 0 ? (
						<TimelineEmptyState label="Nenhuma pendência como responsável no período." />
					) : (
						<div className="rounded-lg border border-kaefer-light-silver">
							<Table className="table-fixed">
								<TableHeader>
									<TableRow className="bg-muted/30">
										<TableHead className="w-24">Data</TableHead>
										<TableHead>Checklist</TableHead>
										<TableHead className="w-28">Status</TableHead>
										<TableHead className="w-24">Prazo</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{data.asResponsible.items.map((item) => (
										<TableRow
											key={item.id}
											className={cn(
												item.status !== 'RESOLVED' && 'bg-kaefer-red/5',
											)}
										>
											<TableCell className="whitespace-nowrap text-muted-foreground">
												{formatDateToDisplay(item.date)}
											</TableCell>
											<TableCell className="whitespace-normal wrap-break-word">
												{item.quizName}
											</TableCell>
											<TableCell>
												<StatusPill status={item.status} />
											</TableCell>
											<TableCell className="whitespace-nowrap text-muted-foreground">
												{formatDateToDisplay(item.dueDate)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)}
				</>
			)}
		</div>
	)
}
